import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PAGINATION_PAGE_SIZE } from 'basics/constants/common.constant';
import { mayaCepCreateRoute } from 'basics/constants/routes.constants';
import { CepCreationMode } from 'basics/enums/cep.enums';
import useGetAllCepQuery from 'basics/graphql/queries/getAllCeps';
import useCepTemplateSelectorFormConfig from 'components/CepTemplateSelectorForm/CepTemplateSelectorForm.config';
import { CepTemplateSelectorFiltersType } from 'components/CepTemplateSelectorForm/CepTemplateSelectorForm.types';
import { SortFilter } from 'generated/maya.types';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCepTemplateSelectorForm = () => {
  const { providerFields } = useYodaCreateForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cepCreationMode, setCepCreationMode] = useState(CepCreationMode.template);
  const [cepTemplateSelectorFilters, setCepTemplateSelectorFilters] = useState<Omit<CepTemplateSelectorFiltersType, 'sort'>>();
  const [cepSortFilter, setCepSortFilter] = useState<SortFilter>(SortFilter.MOST_POPULAR);
  const { cepTemplateSelectorFormConfig } = useCepTemplateSelectorFormConfig(cepCreationMode, setCepCreationMode);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_PAGE_SIZE);

  const queryOptions = useMemo(
    () => ({
      limit: pageSize,
      offset: page * pageSize,
    }),
    [page, pageSize],
  );

  const { data: ceps, loading, count } = useGetAllCepQuery({
    ...cepTemplateSelectorFilters,
    isTemplate: true,
    getPopularity: true,
  },
  queryOptions,
  true,
  cepSortFilter || SortFilter.NEWEST);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: () => { if (cepCreationMode === CepCreationMode.custom) navigate(mayaCepCreateRoute); },
      buttonType: ButtonType.primary,
      disabled: cepCreationMode !== CepCreationMode.custom,
    },
    label: t('actions_continue_label'),
  };

  return {
    cepCreationMode,
    cepTemplateSelectorFormConfig,
    providerFields,
    submitButtonConfig,
    setCepTemplateSelectorFilters,
    cepTemplateSelectorFilters,
    cepSortFilter,
    setCepSortFilter,
    ceps,
    loading,
    count,
    onPageChange,
    onPageSizeChange,
  };
};

export default useCepTemplateSelectorForm;
